import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Search,
  Bot,
  ChevronLeft,
  ChevronRight,
  Globe,
  BookOpen,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import AdminLayout from "@/components/layout/AdminLayout";
import { fetchChatSessions, fetchChatStats } from "@/api/chat";
import { usePagination } from "@/hooks/usePagination";
import { BookSelector } from "@/components/chat/BookSelector";

export default function AIChatSessionsList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [bookFilter, setBookFilter] = useState<number | undefined>();
  const [chatTypeFilter, setChatTypeFilter] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const { data: stats } = useQuery({
    queryKey: ["chat-stats"],
    queryFn: fetchChatStats,
  });

  const { data: sessionsData, isLoading } = useQuery({
    queryKey: ["chat-sessions", { search, bookId: bookFilter, chatType: chatTypeFilter, page, pageSize }],
    queryFn: () =>
      fetchChatSessions({ search, bookId: bookFilter, chatType: chatTypeFilter, page, pageSize }),
  });

  const sessions = sessionsData?.data || [];
  const totalCount = sessionsData?.count || 0;

  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: totalCount,
    itemsPerPage: pageSize,
    currentPage: page,
  });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <AdminLayout
      title="Ask AI Sessions"
      actionButton={
        <Button onClick={() => navigate("/admin/ask-ai/playground")}>
          <Bot className="w-4 h-4 mr-2" />
          Open Playground
        </Button>
      }
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Sessions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats?.totalSessions ?? "..."}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{stats?.totalMessages ?? "..."}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by title..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-9"
          />
        </div>
        <Select
          value={chatTypeFilter || "all"}
          onValueChange={(v) => {
            setChatTypeFilter(v === "all" ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Chat Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="book">Book</SelectItem>
            <SelectItem value="ask_ai">Ask AI</SelectItem>
          </SelectContent>
        </Select>
        <BookSelector
          value={bookFilter}
          onChange={(id) => {
            setBookFilter(id);
            setPage(1);
          }}
          onlyWithEmbeddings={false}
        />
        {(bookFilter || chatTypeFilter) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setBookFilter(undefined);
              setChatTypeFilter(undefined);
              setPage(1);
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Book</TableHead>
                <TableHead>User</TableHead>
                <TableHead className="text-center">Messages</TableHead>
                <TableHead className="text-center">Feedback</TableHead>
                <TableHead>Persona</TableHead>
                <TableHead>Last Activity</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : sessions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No chat sessions found.
                  </TableCell>
                </TableRow>
              ) : (
                sessions.map((session) => (
                  <TableRow
                    key={session.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() =>
                      navigate(`/admin/ask-ai/${session.id}`)
                    }
                  >
                    <TableCell className="font-medium max-w-[200px] truncate">
                      {session.title || "Untitled"}
                    </TableCell>
                    <TableCell>
                      {session.chat_type === "ask_ai" ? (
                        <Badge variant="outline" className="gap-1">
                          <Globe className="w-3 h-3" />
                          AI
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1">
                          <BookOpen className="w-3 h-3" />
                          Book
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate">
                      {session.book_id
                        ? session.book_title || `Book #${session.book_id}`
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <div className="truncate">{session.user_name || session.user_email || session.user_id.slice(0, 8)}</div>
                      <Badge
                        variant={
                          session.subscription_status === "active" ? "default" :
                          session.subscription_status === "expired" ? "destructive" :
                          session.subscription_status === "cancelled" ? "destructive" :
                          "outline"
                        }
                        className="text-[10px] mt-0.5"
                      >
                        {session.subscription_status || "free"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary">{session.message_count}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      {(session.positive_feedback_count > 0 || session.negative_feedback_count > 0) ? (
                        <div className="flex items-center justify-center gap-2 text-xs">
                          {session.positive_feedback_count > 0 && (
                            <span className="flex items-center gap-0.5 text-green-600">
                              <ThumbsUp className="w-3 h-3" />
                              {session.positive_feedback_count}
                            </span>
                          )}
                          {session.negative_feedback_count > 0 && (
                            <span className="flex items-center gap-0.5 text-red-500">
                              <ThumbsDown className="w-3 h-3" />
                              {session.negative_feedback_count}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{session.persona || "librarian"}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {formatDate(session.updated_at)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            {totalCount} session{totalCount !== 1 ? "s" : ""} total
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              disabled={!canGoPrev}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {pageNumbers.map((p) => (
              <Button
                key={p}
                variant={p === page ? "default" : "outline"}
                size="icon"
                onClick={() => setPage(p)}
              >
                {p}
              </Button>
            ))}
            <Button
              variant="outline"
              size="icon"
              disabled={!canGoNext}
              onClick={() => setPage(page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
